import { Box, Flex, HStack, Image, Skeleton, Text, VStack } from "@chakra-ui/react";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type ChartOptions,
  type TooltipItem,
} from "chart.js";
import { useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { DripHistoryItem } from "../../hooks/useDripHistory";
import { ThemeColor } from "../../theme/palette";
import { formatSmartFixed6 } from "../../utils/format";
import { aggregateDripHistoryByDay } from "./aggregateDripHistoryByDay";
import { sortAssetMintsByLatestReward } from "./sortAssetMintsByLatestReward";
import { splitRewardTotal } from "./mergeDripIncentives";
import { toCumulativeSeries } from "./cumulativeRewards";
import {
  DRIP_CHART_FONT,
  DRIP_CHART_GRID_COLOR,
  DRIP_CHART_TICK_COLOR,
  DRIP_CHART_TOOLTIP_BG,
  DRIP_CHART_TOOLTIP_BODY,
  DRIP_CHART_TOOLTIP_TITLE,
  DRIP_TEXT_LABEL,
  DRIP_TEXT_SECONDARY,
  DripTokenMeta,
} from "./dripStyles";
import { DripRewardAssetSelector } from "./DripRewardAssetSelector";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend
);
ChartJS.defaults.font.family = DRIP_CHART_FONT;
ChartJS.defaults.color = DRIP_CHART_TICK_COLOR;

// Fallback decimals when a mint hasn't resolved on-chain yet (kySOL is 9).
const FALLBACK_DECIMALS = 9;

const sectionLabelSx = {
  fontSize: "xs",
  color: DRIP_TEXT_LABEL,
  letterSpacing: "0.08em",
  fontWeight: "medium",
  textTransform: "uppercase" as const,
};

const chartTickFont = {
  family: DRIP_CHART_FONT,
  size: 11,
  weight: 400 as const,
};

const chartTooltipFont = {
  family: DRIP_CHART_FONT,
  size: 12,
};

export type { DripTokenMeta } from "./dripStyles";

interface DripRewardsChartProps {
  history: DripHistoryItem[];
  totalsByMint: Record<string, string>;
  decimalsByMint: Record<string, number>;
  isLoading: boolean;
  tokenMetaByMint: Record<string, DripTokenMeta>;
  /**
   * Bar colour to force for one mint. Series colours are otherwise assigned by
   * rank in the palette, which leaves a partner's own token whatever slot it
   * happens to land in — so a partner page pins its accent here.
   */
  accentByMint?: Record<string, string>;
  /** Mint that leads the tabs regardless of when it last paid out. */
  pinnedMint?: string;
  /**
   * The incentive-campaign share of `totalsByMint`, used to break the total
   * into its drip and rewards legs. Absent mints are all drip.
   */
  incentiveTotalsByMint?: Record<string, string>;
}

/**
 * Translucent version of a series colour, for the area under the line. Returns
 * undefined for anything that is not a 6-digit hex, so the fill is simply
 * skipped rather than rendering a broken colour.
 */
const hexToRgba = (hex: string, alpha: number): string | undefined => {
  const match = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return undefined;

  const value = parseInt(match[1], 16);
  // eslint-disable-next-line no-bitwise
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
};

const formatAxisAmount = (value: number) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  if (value >= 1) return value.toFixed(2);
  if (value > 0) return value.toFixed(4);
  return "0";
};

export const DripRewardsChart = ({
  history,
  totalsByMint,
  decimalsByMint,
  isLoading,
  tokenMetaByMint,
  accentByMint,
  pinnedMint,
  incentiveTotalsByMint,
}: DripRewardsChartProps) => {
  const resolveLabel = (mint: string) =>
    tokenMetaByMint[mint]?.symbol ?? `${mint.slice(0, 4)}...${mint.slice(-4)}`;

  const formatRawTokenAmount = (raw: string, mint: string) =>
    formatSmartFixed6(Number(raw) / Math.pow(10, decimalsByMint[mint] ?? FALLBACK_DECIMALS));

  const chartData = useMemo(
    () => aggregateDripHistoryByDay(history, resolveLabel, decimalsByMint),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, tokenMetaByMint, decimalsByMint]
  );

  const assetMints = useMemo(() => {
    const mintSet = new Set([
      ...Object.keys(totalsByMint),
      ...(chartData?.series.map((series) => series.mint) ?? []),
    ]);

    return sortAssetMintsByLatestReward([...mintSet], history, pinnedMint);
  }, [totalsByMint, chartData, history, pinnedMint]);

  const [selectedMint, setSelectedMint] = useState<string | null>(null);

  const activeMint = selectedMint ?? assetMints[0] ?? null;
  const activeMeta = activeMint ? tokenMetaByMint[activeMint] : null;
  const activeTotalRaw = activeMint ? totalsByMint[activeMint] : undefined;

  // Only worth breaking out once a campaign has actually paid: on every other
  // asset the total is the drip leg and a split would say nothing.
  const activeSplit =
    activeMint && incentiveTotalsByMint?.[activeMint]
      ? splitRewardTotal(activeTotalRaw, incentiveTotalsByMint[activeMint])
      : null;
  const hasSplit = !!activeSplit && activeSplit.incentiveRaw !== "0";
  const activeSeries = chartData?.series.find((series) => series.mint === activeMint) ?? null;

  // The daily series, kept alongside the running total so the tooltip can say
  // what a given day actually paid.
  const dailyData = activeSeries?.data ?? null;

  const lineChartData = useMemo(() => {
    if (!chartData || !activeSeries) return null;

    const color = accentByMint?.[activeSeries.mint] ?? activeSeries.color;
    const fill = hexToRgba(color, 0.12);

    return {
      labels: chartData.labels,
      datasets: [
        {
          label: activeSeries.label,
          data: toCumulativeSeries(activeSeries.data),
          borderColor: color,
          backgroundColor: fill ?? "transparent",
          fill: !!fill,
          borderWidth: 2,
          tension: 0.25,
          // A point per calendar day is noise on a months-long range; the
          // hover point is enough to read a single day off the line.
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: color,
          pointHoverBorderColor: color,
        },
      ],
    };
  }, [chartData, activeSeries, accentByMint]);

  const options: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: "index",
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: DRIP_CHART_TOOLTIP_BG,
          titleColor: DRIP_CHART_TOOLTIP_TITLE,
          bodyColor: DRIP_CHART_TOOLTIP_BODY,
          borderColor: "rgba(255, 255, 255, 0.12)",
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: false,
          titleFont: { ...chartTooltipFont, weight: 600 },
          bodyFont: { ...chartTooltipFont, weight: 400 },
          callbacks: {
            title: (items: TooltipItem<"line">[]) => items[0]?.label ?? "",
            // The line is a running total, so the day's own payout would
            // otherwise be unreadable — it is the step, not the height.
            label: (item: TooltipItem<"line">) => {
              const symbol = activeMeta?.symbol ?? "Reward";
              const total = `Total: ${formatSmartFixed6(Number(item.raw))} ${symbol}`;
              const paid = dailyData?.[item.dataIndex] ?? 0;

              return paid > 0
                ? [total, `Paid this day: +${formatSmartFixed6(paid)} ${symbol}`]
                : total;
            },
          },
          padding: 10,
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: DRIP_CHART_TICK_COLOR,
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
            font: chartTickFont,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: DRIP_CHART_GRID_COLOR,
          },
          ticks: {
            color: DRIP_CHART_TICK_COLOR,
            callback: (value) => formatAxisAmount(Number(value)),
            font: chartTickFont,
          },
        },
      },
    }),
    [activeMeta?.symbol, dailyData]
  );

  if (isLoading) {
    return (
      <VStack spacing={4} align="stretch">
        <Skeleton height="24px" width="180px" />
        <Skeleton height="280px" width="100%" borderRadius="xl" />
      </VStack>
    );
  }

  if (assetMints.length === 0) {
    return (
      <Text fontSize="sm" color={DRIP_TEXT_LABEL}>
        No rewards distributed yet.
      </Text>
    );
  }

  const hasMultipleAssets = assetMints.length > 1;

  return (
    <VStack spacing={4} align="stretch">
      {hasMultipleAssets && (
        <DripRewardAssetSelector
          assetMints={assetMints}
          activeMint={activeMint ?? assetMints[0]}
          onSelectMint={setSelectedMint}
          tokenMetaByMint={tokenMetaByMint}
          totalsByMint={totalsByMint}
          decimalsByMint={decimalsByMint}
          resolveLabel={resolveLabel}
        />
      )}

      <VStack spacing={4} align="stretch" width="100%">
        <Flex
          justify="space-between"
          align={hasSplit ? "start" : "center"}
          gap={3}
          py={3}
          borderTop="1px solid"
          borderBottom="1px solid"
          borderColor="whiteAlpha.100"
          width="100%"
        >
          <Text {...sectionLabelSx}>Total accumulated</Text>
          <VStack spacing={0.5} align="end" minW={0}>
            <HStack spacing={2}>
              {activeMeta?.image && (
                <Image src={activeMeta.image} boxSize="18px" borderRadius="full" />
              )}
              <Text fontSize="lg" fontWeight="semibold" color={ThemeColor.KAIROS}>
                {activeMint && activeTotalRaw
                  ? formatRawTokenAmount(activeTotalRaw, activeMint)
                  : "0"}
              </Text>
              <Text fontSize="sm" fontWeight="medium" color={DRIP_TEXT_SECONDARY}>
                {activeMeta?.symbol ?? "—"}
              </Text>
            </HStack>
            {hasSplit && activeMint && (
              <Text fontSize="xs" color={DRIP_TEXT_LABEL}>
                Drip {formatRawTokenAmount(activeSplit!.dripRaw, activeMint)} ·
                Rewards {formatRawTokenAmount(activeSplit!.incentiveRaw, activeMint)}
              </Text>
            )}
          </VStack>
        </Flex>

        <Box width="100%">
          <Text {...sectionLabelSx} mb={3}>
            Rewards over time
          </Text>
          {!lineChartData ? (
            <Text fontSize="sm" color={DRIP_TEXT_LABEL}>
              No reward data yet for this asset.
            </Text>
          ) : (
            <Box height="280px" width="100%">
              <Line data={lineChartData} options={options} />
            </Box>
          )}
        </Box>
      </VStack>
    </VStack>
  );
};
